import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { bizcalAdminGet, bizcalAdminSave, PolicyAdmin, PolicyField, PolicyGroup, PolicyKey } from '../../utils/api/bizcal';
import { clearToDefaults, keepsSomeOnClear } from './bizcalPolicyForm';

// Admin → Companies → <firm> → Billing & reminders: the firm's own business calendar (billing day,
// payment terms, reminder offsets, trial, petty-cash timing). Read by the server through
// services/firmPolicy.ts; an empty box = the product default, shown as the placeholder. The property-tax
// fields have no product default (empty = not set), so "Clear all (use defaults)" leaves them untouched.

const input: React.CSSProperties = {
  width: '100%', padding: '9px 12px', fontSize: 13, borderRadius: 'var(--radius-md)',
  border: '1px solid var(--border-strong)', background: 'var(--c-gray-50)', color: 'var(--text-primary)', outline: 'none', boxSizing: 'border-box',
};
const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--text-secondary)', display: 'block', marginBottom: 4 };
const card: React.CSSProperties = { background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-xl)', padding: 20, marginBottom: 16 };
const btn = (bg: string, color = '#fff'): React.CSSProperties => ({ padding: '8px 14px', borderRadius: 'var(--radius-md)', border: 'none', background: bg, color, fontSize: 13, fontWeight: 600, cursor: 'pointer' });

const GROUPS: PolicyGroup[] = ['billing', 'reminders', 'calendar', 'onboarding', 'jobs', 'trial', 'petty', 'proptax'];
const unitKey = (f: PolicyField): string =>
  f.kind === 'day' ? 'bc_u_day' : f.kind === 'list' ? 'bc_u_list' : f.kind === 'amount' ? 'bc_u_amount' : f.kind === 'mmdd' ? 'bc_u_mmdd'
    : f.key === 'trialDocLimit' ? 'bc_u_docs' : /Minutes$/.test(f.key) ? 'bc_u_minutes' : 'bc_u_days';
const show = (v: number | number[] | string | null | undefined): string => (Array.isArray(v) ? v.join(', ') : v == null ? '' : String(v));
const modeOf = (f: PolicyField): 'text' | 'decimal' | 'numeric' => (f.kind === 'list' || f.kind === 'mmdd' ? 'text' : f.kind === 'amount' ? 'decimal' : 'numeric');

export default function BizcalPolicyCard({ companyId }: { companyId: string }) {
  const { t } = useTranslation();
  const [data, setData] = useState<PolicyAdmin | null>(null);
  const [vals, setVals] = useState<Partial<Record<PolicyKey, string>>>({});
  const [err, setErr] = useState(''); const [msg, setMsg] = useState(''); const [saving, setSaving] = useState(false);

  useEffect(() => {
    let live = true;
    setData(null); setErr(''); setMsg('');
    bizcalAdminGet(companyId).then(d => { if (!live) return; setData(d); setVals({ ...d.own }); }).catch(e => live && setErr(e.message));
    return () => { live = false; };
  }, [companyId]);

  const save = async () => {
    if (!data || saving) return;
    setSaving(true); setErr(''); setMsg('');
    try {
      const patch: Partial<Record<PolicyKey, string>> = {};
      for (const f of data.fields) patch[f.key] = String(vals[f.key] ?? '').trim();
      const r = await bizcalAdminSave(companyId, patch);
      setData({ ...data, own: r.own, effective: r.effective }); setVals({ ...r.own });
      setMsg(t('bc_saved'));
    } catch (e: any) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div style={card}>
      <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', marginBottom: 4 }}>{t('bc_title')}</div>
      <div style={{ fontSize: 12, color: 'var(--text-tertiary)', marginBottom: 14 }}>{t('bc_hint')}</div>
      {!data && !err && <div style={{ fontSize: 12, color: 'var(--text-tertiary)' }}>…</div>}
      {data && GROUPS.map(g => {
        const fields = data.fields.filter(f => f.group === g);
        if (!fields.length) return null;
        return (
          <div key={g} style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)', margin: '4px 0 8px' }}>{t(`bc_g_${g}`)}</div>
            {g === 'billing' && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t('bc_billing_note')}</div>}
            {g === 'proptax' && <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 8 }}>{t('bc_proptax_note')}</div>}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              {fields.map(f => (
                <div key={f.key}>
                  <label style={lbl}>{t(`bc_f_${f.key}`)}</label>
                  <input value={vals[f.key] ?? ''} onChange={e => { const v = e.target.value; setVals(p => ({ ...p, [f.key]: v })); setMsg(''); }}
                    inputMode={modeOf(f)} placeholder={data.defaults[f.key] == null ? (f.kind === 'mmdd' ? t('bc_ph_mmdd') : '') : show(data.defaults[f.key])} style={input} />
                  <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginTop: 3 }}>
                    {t(unitKey(f))} · {data.defaults[f.key] == null ? t('bc_no_default') : t('bc_default', { v: show(data.defaults[f.key]) })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
      {err && <div style={{ fontSize: 12, color: '#E24B4A', background: '#FCEBEB', padding: '8px 12px', borderRadius: 'var(--radius-md)', marginBottom: 10 }}>{err}</div>}
      {data && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
          {msg && <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{msg}</span>}
          <button onClick={() => { setVals(v => clearToDefaults(data, v)); setMsg(''); }} title={keepsSomeOnClear(data) ? t('bc_use_defaults_keeps') : undefined}
            style={btn('var(--c-gray-100)', 'var(--text-secondary)')}>{t('bc_use_defaults')}</button>
          <button onClick={save} disabled={saving} style={btn('var(--accent)')}>{saving ? t('bc_saving') : t('bc_save')}</button>
        </div>
      )}
    </div>
  );
}
